import {tokens} from '@pleo-io/telescope'
import GithubSlugger from 'github-slugger'
import {RichText as RichTextRenderer, RichTextProps, Elements} from 'prismic-reactjs'
import React, {FC} from 'react'
import styled, {css} from 'styled-components'

import Image from '@helios/shared/components/image'
import PrismicLink from '@helios/shared/components/prismic-link'
import {useContentVariant} from '@helios/shared/providers/copy-experiments'
import {Text, TextProps, TextLink, TextLinkProps} from '@helios/shared/styles/typography'
import mq from '@helios/shared/utils/media-query'

export interface Props extends TextProps, TextLinkProps {
    content: string | RichTextProps['render']
    withQuotes?: boolean
    as?: keyof JSX.IntrinsicElements | React.ComponentType<any>
    className?: string
    id?: string
    htmlFor?: string
}

const RichText: FC<Props> = ({
    content,
    size,
    weight,
    font,
    color,
    center,
    upperCase,
    bgColor,
    withQuotes,
    as,
    className,
    id,
    htmlFor,
}) => {
    const textProps = {size, weight, font, color, center, upperCase, as, className}
    const {content: renderedContent, isResolved} = useContentVariant(content, id)
    const slugger = new GithubSlugger()

    if (!isResolved) {
        return (
            <Text {...textProps} htmlFor={htmlFor}>
                {'\u00A0'}
            </Text>
        )
    }

    if (typeof renderedContent === 'string') {
        return (
            <Text {...textProps} htmlFor={htmlFor}>
                {withQuotes ? `"${renderedContent}"` : renderedContent}
            </Text>
        )
    } else if (hasRichText(renderedContent)) {
        return (
            <Wrapper withQuotes={withQuotes} className={className}>
                <RichTextRenderer
                    render={renderedContent}
                    htmlSerializer={(type, element, content, children, key) => {
                        switch (type) {
                            case Elements.image: {
                                return (
                                    <Image
                                        key={key}
                                        image={element}
                                        width={element.dimensions.width}
                                        height={element.dimensions.height}
                                    />
                                )
                            }

                            case Elements.paragraph: {
                                return (
                                    <Text {...textProps} key={key} htmlFor={htmlFor}>
                                        {children}
                                    </Text>
                                )
                            }

                            case Elements.hyperlink: {
                                return (
                                    <TextLink
                                        {...element.data}
                                        {...textProps}
                                        bgColor={bgColor}
                                        key={key}
                                        as={PrismicLink}
                                    >
                                        {children}
                                    </TextLink>
                                )
                            }

                            case Elements.heading2: {
                                const id = slugger.slug(element.text.trim())
                                return (
                                    <Text {...textProps} size="md" as="h2" id={id} key={key}>
                                        {children}
                                    </Text>
                                )
                            }

                            case Elements.heading3: {
                                const id = slugger.slug(element.text.trim())
                                return (
                                    <Text
                                        {...textProps}
                                        weight="semiBold"
                                        as="h3"
                                        key={key}
                                        id={id}
                                    >
                                        {children}
                                    </Text>
                                )
                            }

                            case Elements.heading4: {
                                const id = slugger.slug(element.text.trim())
                                return (
                                    <Text
                                        {...textProps}
                                        as="h4"
                                        color="lightMuted"
                                        size="sm"
                                        key={key}
                                        id={id}
                                    >
                                        {children}
                                    </Text>
                                )
                            }

                            case Elements.heading5: {
                                return (
                                    <Text {...textProps} as="h5" size="xs" key={key}>
                                        {children}
                                    </Text>
                                )
                            }

                            case Elements.heading6: {
                                return (
                                    <Text {...textProps} as="h6" size="xs" key={key}>
                                        {children}
                                    </Text>
                                )
                            }

                            case Elements.oListItem:
                            case Elements.listItem: {
                                return (
                                    <Text {...textProps} as="li" key={key}>
                                        <span>{children}</span>
                                    </Text>
                                )
                            }

                            case Elements.list: {
                                return <UL key={key}>{children}</UL>
                            }

                            case Elements.oList: {
                                return <OL key={key}>{children}</OL>
                            }

                            default: {
                                return null
                            }
                        }
                    }}
                />
            </Wrapper>
        )
    } else {
        return null
    }
}

export const hasRichText = (content: Props['content']) => {
    if (typeof content === 'string') {
        return !!content
    } else {
        return !!content && !!RichTextRenderer.asText(content)
    }
}

const listStyles = css`
    display: grid;
    gap: ${tokens.spacing16};

    ${mq.min('md')} {
        gap: ${tokens.spacing28};
    }
`

const UL = styled.ul`
    ${listStyles}
`

const OL = styled.ol`
    ${listStyles}
`

type WrapperProps = Pick<Props, 'withQuotes'>

const withQuotes = css`
    p:first-child:before {
        content: '"';
    }

    p:last-child:after {
        content: '"';
    }
`

const Wrapper = styled.div<WrapperProps>`
    > * {
        margin: 0;
    }

    && > * + * {
        margin-top: ${tokens.spacing28};

        ${mq.min('md')} {
            margin-top: ${tokens.spacing40};
        }
    }

    && > h2 + *,
    && > h3 + *,
    && > h4 + *,
    && > h5 + *,
    && > h6 + * {
        margin-top: ${tokens.spacing12};
    }

    label {
        display: block;
    }

    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
        a {
            font: inherit;
        }
    }

    ${(props) => props.withQuotes && withQuotes}
`

export default RichText
